import React from "react";

export default function PrivacyContent() {
  const updated = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const H2 = ({ id, children }) => (
    <h2
      id={id}
      className="group text-white text-xl font-semibold tracking-tight"
    >
      <span>{children}</span>
      <a
        href={`#${id}`}
        className="ml-2 align-middle text-slate-400 opacity-0 group-hover:opacity-100 hover:text-slate-200 transition"
        aria-label="Link to section"
      >
        #
      </a>
    </h2>
  );

  const Bullets = ({ children }) => (
    <ul className="list-disc pl-5 space-y-2 text-slate-300">{children}</ul>
  );

  return (
    <article className="mx-auto max-w-3xl text-slate-300 leading-relaxed">
      {/* Last updated */}
      <p className="text-sm text-slate-400">Last updated: {updated}</p>

      <div className="mt-4 divide-y divide-white/10">
        {/* Title line */}
        <section className="pt-6 first:pt-0">
          <p className="text-white text-lg font-semibold">
            Hey Let’s Travel - Privacy Policy
          </p>
        </section>

        {/* 1 */}
        <section className="pt-6">
          <H2 id="who-we-are">1. Who We Are</H2>
          <p className="mt-2">
            Hey Let’s Travel Ltd (“we”, “us”, “our”) is a UK-registered lifestyle and
            events company operating across music, travel, and entertainment. We run
            ticketed events, artist management and subscription services, affiliate and
            sponsorship programs, and concierge travel experiences.
          </p>
          <p className="mt-2">
            We are the data controller responsible for the personal information collected
            through our website, apps, and digital services (collectively, the “Service”).
            For any privacy-related questions, contact:{" "}
            <a
              href="mailto:support@hey-lets-travel.com"
              className="underline decoration-white/20 hover:decoration-white text-slate-200"
            >
              support@hey-lets-travel.com
            </a>
            .
          </p>
        </section>

        {/* 2 */}
        <section className="pt-6">
          <H2 id="what-data-we-collect">2. What Data We Collect</H2>

          <p className="mt-2 font-semibold text-slate-200">Account &amp; Identity Data</p>
          <Bullets>
            <li>Name, email, password hash (we never store plain passwords)</li>
            <li>Date of birth (18+ verification)</li>
            <li>Contact details and communication preferences</li>
          </Bullets>

          <p className="mt-4 font-semibold text-slate-200">Ticketing &amp; Event Data</p>
          <Bullets>
            <li>Ticket purchases, event attendance, and QR entry data</li>
            <li>Affiliate codes and referral links used for sales tracking</li>
            <li>Guest lists, booking references, and travel package selections</li>
          </Bullets>

          <p className="mt-4 font-semibold text-slate-200">Subscription &amp; Artist Management Data</p>
          <Bullets>
            <li>Subscription tier and billing details</li>
            <li>Artist profile information, media links, uploads, and promotional materials</li>
            <li>Assigned personal manager, monthly activity reports, and participation metrics</li>
          </Bullets>

          <p className="mt-4 font-semibold text-slate-200">Payment Data</p>
          <Bullets>
            <li>
              Payments processed securely via Stripe – we store customer and subscription IDs
              only, not full card numbers
            </li>
            <li>Transaction amounts, timestamps, and refund/chargeback history</li>
          </Bullets>

          <p className="mt-4 font-semibold text-slate-200">Marketing &amp; Communication Data</p>
          <Bullets>
            <li>Email preferences, affiliate communication, and campaign engagement</li>
            <li>Any messages, feedback, or application forms sent through our site</li>
          </Bullets>

          <p className="mt-4 font-semibold text-slate-200">Technical Data</p>
          <Bullets>
            <li>IP address, device type, browser info, session duration, and basic analytics</li>
            <li>Cookies used for login continuity, checkout flow, and fraud prevention</li>
          </Bullets>

          <p className="mt-2">
            We do not use third-party advertising cookies or sell your personal data.
          </p>
        </section>

        {/* 3 */}
        <section className="pt-6">
          <H2 id="how-we-use-your-data">3. How We Use Your Data</H2>
          <Bullets>
            <li>Create and manage your HLT account securely</li>
            <li>Process ticket, subscription, and booking payments</li>
            <li>Assign artist managers and track affiliate performance</li>
            <li>Deliver event access via QR code and manage attendance records</li>
            <li>Send necessary communications (verification, receipts, event updates)</li>
            <li>Prevent fraud, maintain security, and comply with financial regulations</li>
            <li>Improve our platform, content delivery, and user experience</li>
          </Bullets>
        </section>

        {/* 4 */}
        <section className="pt-6">
          <H2 id="legal-bases">4. Legal Bases (UK GDPR)</H2>
          <Bullets>
            <li>
              <strong>Contract</strong> – to deliver services you request (tickets,
              subscriptions, concierge bookings).
            </li>
            <li>
              <strong>Legitimate Interests</strong> – security, fraud prevention,
              marketing our own events, and improving user experience.
            </li>
            <li>
              <strong>Legal Obligation</strong> – for tax, accounting, and regulatory
              requirements.
            </li>
            <li>
              <strong>Consent</strong> – where required (e.g., marketing opt-ins).
            </li>
          </Bullets>
        </section>

        {/* 5 */}
        <section className="pt-6">
          <H2 id="who-we-share-with">5. Who We Share Data With</H2>
          <p className="mt-2">We share data only when necessary to operate the Service:</p>
          <Bullets>
            <li>Stripe – payment and subscription processing</li>
            <li>Email providers – to deliver verification, updates, and receipts</li>
            <li>Hosting &amp; database providers – for website and app functionality</li>
            <li>Event partners – where needed to validate ticket entry or manage guest access</li>
            <li>Travel &amp; accommodation partners – when you book concierge packages</li>
            <li>Law enforcement or regulators – if legally required</li>
          </Bullets>
          <p className="mt-2">
            All partners are bound by data-processing agreements ensuring GDPR compliance.
          </p>
        </section>

        {/* 6 */}
        <section className="pt-6">
          <H2 id="international-transfers">6. International Transfers</H2>
          <p className="mt-2">
            Our partners (e.g., Stripe, hosting providers) may store or process data in
            the EEA, UK, or other countries with adequate protection standards. When
            transfers occur, we rely on safeguards such as Standard Contractual Clauses
            or adequacy decisions.
          </p>
        </section>

        {/* 7 */}
        <section className="pt-6">
          <H2 id="data-retention">7. Data Retention</H2>
          <Bullets>
            <li>
              <strong>Account data:</strong> kept while your account is active. Deleted or
              anonymised when closed.
            </li>
            <li>
              <strong>Transaction data:</strong> retained for up to 6 years for tax and
              accounting.
            </li>
            <li>
              <strong>Artist/affiliate data:</strong> retained while active and removed
              upon request or inactivity termination.
            </li>
            <li>
              <strong>Security logs:</strong> retained short-term for system integrity.
            </li>
          </Bullets>
        </section>

        {/* 8 */}
        <section className="pt-6">
          <H2 id="security">8. Security</H2>
          <Bullets>
            <li>Password hashing (bcrypt)</li>
            <li>Two-factor authentication (optional)</li>
            <li>Encrypted data transmission (HTTPS/TLS)</li>
            <li>httpOnly cookies for authentication</li>
            <li>Access control for staff and partners</li>
          </Bullets>
        </section>

        {/* 9 */}
        <section className="pt-6">
          <H2 id="your-rights">9. Your Rights</H2>
          <Bullets>
            <li>Access a copy of your personal data</li>
            <li>Correct or delete inaccurate information</li>
            <li>Withdraw consent or restrict processing</li>
            <li>Request data portability</li>
            <li>Object to certain uses (e.g., direct marketing)</li>
          </Bullets>
          <p className="mt-2">
            To exercise these rights, email{" "}
            <a
              href="mailto:support@hey-lets-travel.com"
              className="underline decoration-white/20 hover:decoration-white text-slate-200"
            >
              support@hey-lets-travel.com
            </a>
            . You can also complain to the Information Commissioner’s Office (ICO) if you
            believe your data has been misused.
          </p>
        </section>

        {/* 10 */}
        <section className="pt-6">
          <H2 id="children">10. Children</H2>
          <p className="mt-2">
            Our services are strictly for users aged 18 and over. We do not knowingly
            collect data from minors.
          </p>
        </section>

        {/* 11 */}
        <section className="pt-6">
          <H2 id="updates">11. Updates to This Policy</H2>
          <p className="mt-2">
            We may update this Privacy Policy periodically. The latest version will always
            be available at hey-lets-travel.com/privacy, with the “Last Updated” date
            shown above. Continued use of our services indicates acceptance of the updated
            policy.
          </p>
        </section>

        {/* 12 */}
        <section className="pt-6">
          <H2 id="contact">12. Contact</H2>
          <p className="mt-2">
            Hey Let’s Travel Ltd<br />
            Email:{" "}
            <a
              href="mailto:support@hey-lets-travel.com"
              className="underline decoration-white/20 hover:decoration-white text-slate-200"
            >
              support@hey-lets-travel.com
            </a>
          </p>
        </section>
      </div>
    </article>
  );
}
